import React, { useCallback, useMemo, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { makeStyles, Theme, Typography } from '@material-ui/core';
import { AddCircle, RemoveCircle } from '@material-ui/icons';
import {
  PEGASUS_CUSTOM_ROW_HEIGHT,
  PEGASUS_FREE_COL_MIN_WIDTH,
  PEGASUS_HEADER_HEIGHT,
  pegasusTimeTableColumns,
  SentinelPressureOptionsFn,
  SentinelPressureTypes,
  TimeTypeOptionsFn,
  TimeTypeType,
  PEGASUS_MAX_HEIGHT,
  getHours,
  getMinutes,
} from './PegasusTables.utils';
import { PegasusTimeStatusesModel, PressureUnitsOptionsFn } from '@shared/models';
import { indexOf, isEmpty } from 'lodash';
import { usePmZoneSiteListDataSource } from '@features/dataSource';
import { useFormikContext } from 'formik';
import { useAppSelector } from '@core/store/store';
import { preferencesSelector } from '@core/store/entity/preferences/PreferencesSelectors';
import { SettingsFormModel, UseStateModel } from '@features/ControlView/sections/Tabs/ControlTabs.models';
import { useNotifications } from '@shared/hooks/useNotifications';
import {
  Button,
  ConfirmButton,
  CustomInput,
  CustomSelect,
  CustomSelectItem,
  DataTable,
  DataTableColumn,
  PromptType,
} from '@hwm/ui-lib';

const useStyles = makeStyles((theme: Theme) => ({
  select: {
    height: 28,
    maxHeight: 28,
    borderWidth: 2,
    zIndex: 200,
    margin: theme.spacing(0),
    padding: 0,
    '& .select__control': {
      minWidth: 50,
    },
  },
}));

export interface PegasusTimeTableProps extends UseStateModel<PegasusTimeStatusesModel[]> {
  disableInputs?: boolean;
  editMode?: boolean;
  inLearning?: boolean;
  maxHeight?: number;
  isForPmzDetails?: boolean;
  /**
   * `dataTestId` - name of a pegasus time table (preferably `<name>-pegasus-time-table`)
   * @example
   * ```tsx
   * <PegasusTimeTable {...props} dataTestId="test-pegasus-time-table" />
   */
  dataTestId: string;
}

export const PegasusTimeTable: React.FC<PegasusTimeTableProps> = ({
  data: passedData,
  setData,
  disableInputs = false,
  editMode = false,
  inLearning = false,
  maxHeight = PEGASUS_MAX_HEIGHT,
  isForPmzDetails = false,
  dataTestId,
}) => {
  const classes = useStyles();

  const SentinelPressureOptions = useMemo(() => SentinelPressureOptionsFn(), []);

  const { values } = useFormikContext<SettingsFormModel>() ?? {
    values: {} as SettingsFormModel,
  };

  const [timeType, setTimeType] = useState<TimeTypeType>(TimeTypeType.EveryDay);
  const [time, setTime] = useState('00:00');
  const [pressure, setPressure] = useState('');
  const [pressureType, setPressureType] = useState<SentinelPressureTypes>(SentinelPressureTypes.Default);

  const { pmZoneSites } = usePmZoneSiteListDataSource();

  const findSite = pmZoneSites?.find((val) =>
    isForPmzDetails ? val.isAssignedToPmZone && val.siteType === 'PRV' : val.id === values.prvId
  );
  const isSentinel2 = findSite?.loggerType?.includes('Sentinel 2');

  const data = passedData?.map((el) => ({
    ...el,
    uiPressure: isSentinel2
      ? SentinelPressureOptions[Number(el.pressure) === 0 ? SentinelPressureTypes.Low : SentinelPressureTypes.High]
      : Number(el.pressure)?.toFixed(2),
  }));

  const { preferences: units } = useAppSelector(preferencesSelector);

  const { handleError } = useNotifications();

  const handleAddingTime = useCallback(
    (timeValue: string, pressureValue: string, timeType: TimeTypeType) => {
      const validData =
        timeType &&
        timeValue &&
        (pressureValue || pressureType) &&
        Number.isFinite(Number(pressureValue)) &&
        Number(pressureValue) >= 0 &&
        (!pressureValue.includes('.') || pressureValue.split('.')?.[1]?.length <= 2);

      if (validData) {
        const dataToAdd = {
          timeType,
          time: timeValue,
          pressure: isSentinel2 ? pressureType : pressureValue,
          uiPressure: isSentinel2 ? SentinelPressureOptions[pressureType] : Number(pressureValue).toFixed(2),
        } as PegasusTimeStatusesModel;

        const exists = data.some((el) => el.timeType === dataToAdd.timeType && el.time === dataToAdd.time);
        if (exists) {
          handleError(
            t`Cannot add new row to Time table. Time Type and Time cannot be the same in the table`,
            t`TIME TABLE`
          );
        } else {
          if (
            (!values.actuatorMin || Number(dataToAdd.pressure) >= values.actuatorMin) &&
            (!values.actuatorMax || Number(dataToAdd.pressure) <= values.actuatorMax)
          ) {
            setData((prev) => [...prev, dataToAdd]);
            setTime('00:00');
            setPressure('');
            setPressureType(SentinelPressureTypes.Default);
          } else {
            handleError(t`Your table values must be between your actuator range`, t`TIME TABLE`);
          }
        }
      } else {
        handleError(
          !isSentinel2
            ? t`Cannot add new row to Time table. Pressure should be a positive number with at most 2 decimal places, Time Type and Time values should be selected`
            : t`The pressure cannot be empty`,
          t`TIME TABLE`
        );
      }
    },
    [
      setData,
      handleError,
      data,
      isSentinel2,
      pressureType,
      values.actuatorMin,
      values.actuatorMax,
      SentinelPressureOptions,
    ]
  );

  return (
    <>
      <div className="grid" data-testid={`${dataTestId}-wrapper`}>
        <DataTable
          columns={[
            ...pegasusTimeTableColumns(PressureUnitsOptionsFn()[units.pressureUnit], editMode, isSentinel2),
            ...(editMode
              ? [
                  {
                    Header: '',
                    accessor: 'action' as any,
                    fixed: 'right',
                    width: 40,
                    renderCell: ({ data: row, dataTestId }) => (
                      <RemoveCircle
                        color="primary"
                        className="z-10 cursor-pointer"
                        onClick={() => {
                          const id = indexOf(data, row);

                          setData((prev) => prev.filter((_, i) => id !== i));
                        }}
                        data-testid={`${dataTestId}-remove-row-button`}
                      />
                    ),
                  } as DataTableColumn<PegasusTimeStatusesModel>,
                ]
              : []),
          ]}
          data={[...data]?.sort(handleSortTable) ?? []}
          freeColMinWidth={PEGASUS_FREE_COL_MIN_WIDTH}
          customRowHeight={PEGASUS_CUSTOM_ROW_HEIGHT}
          customHeaderHeight={PEGASUS_HEADER_HEIGHT}
          heightToContent
          maxHeight={maxHeight}
          dataTestId={`${dataTestId}`}
        />
      </div>

      {(editMode || inLearning) && Boolean(data) && !isEmpty(data) ? (
        <ConfirmButton
          promptProps={{
            content: (
              <div className="space-y-3">
                <Typography>
                  <Trans>Are you sure you want to clear the current table?</Trans>
                </Typography>

                <Typography variant="body2" className="text-center italic">
                  <Trans>In order to use the table you need to Submit Control Settings</Trans>
                </Typography>
              </div>
            ),
            title: t`Clear Time Table`,
            type: PromptType.Warning,
            onConfirm: () => {
              setData([]);
            },
          }}
          dataTestId={`${dataTestId}-clear-confirm-prompt`}
        >
          {({ onClick }) => (
            <Button
              style={{ height: 32, marginLeft: 'auto', padding: 8 }}
              variant="text"
              endIcon={<RemoveCircle color="primary" />}
              onClick={onClick}
              dataTestId={`${dataTestId}-clear-confirm-prompt-button`}
            >
              <Trans>Clear Time Table</Trans>
            </Button>
          )}
        </ConfirmButton>
      ) : null}

      {editMode && (
        <div data-testid={`${dataTestId}-add-new-entry-wrapper`}>
          <Typography data-testid={`${dataTestId}-add-new-entry-title`}>
            <Trans>Add a new entry</Trans>:
          </Typography>

          <DataTable
            columns={[
              {
                Header: t`Time Type`,
                accessor: 'timeType',
                width: 124,
                renderCell: ({ dataTestId }) => (
                  <CustomSelect
                    label=""
                    onChange={(selectedValue) => {
                      setTimeType((selectedValue as CustomSelectItem).value);
                    }}
                    options={Object.entries(TimeTypeOptionsFn()).map(([value, label]) => ({
                      label,
                      value,
                    }))}
                    minWidth={108}
                    className={classes.select}
                    value={{ label: TimeTypeOptionsFn()[timeType], value: timeType }}
                    isDisabled={disableInputs}
                    isError={false}
                    dataTestId={`${dataTestId}-time-type-select`}
                  />
                ),
              },
              {
                Header: t`Time`,
                accessor: 'time',
                width: 124,
                renderCell: ({ dataTestId }) => (
                  <CustomInput
                    name="time"
                    type="time"
                    value={time ?? '00:00'}
                    minWidth={120}
                    maxWidth={120}
                    onChange={(e) => setTime(e.target.value)}
                    onEnterPress={() => handleAddingTime(time, isSentinel2 ? pressureType : pressure, timeType)}
                    disabled={disableInputs}
                    customBorderColor="#cccccc"
                    dataTestId={`${dataTestId}-time-input`}
                  />
                ),
              },
              {
                Header: t`Pressure`,
                accessor: 'pressure',
                renderCell: ({ dataTestId }) =>
                  !isSentinel2 ? (
                    <CustomInput
                      name="pressure"
                      value={pressure}
                      minWidth={98}
                      maxWidth={160}
                      onChange={(e) => setPressure(e.target.value)}
                      onEnterPress={() => handleAddingTime(time, pressure, timeType)}
                      disabled={disableInputs}
                      customBorderColor="#cccccc"
                      dataTestId={`${dataTestId}-pressure-input`}
                    />
                  ) : (
                    <CustomSelect
                      label={null}
                      onChange={(selectedValue) => {
                        setPressureType((selectedValue as CustomSelectItem).value);
                      }}
                      options={Object.entries(SentinelPressureOptions).map(([value, label]) => ({
                        label,
                        value,
                      }))}
                      minWidth={108}
                      className={classes.select}
                      value={{ label: SentinelPressureOptions[pressureType], value: pressureType }}
                      isDisabled={disableInputs}
                      isError={false}
                      dataTestId={`${dataTestId}-pressure-select`}
                    />
                  ),
              },
              {
                Header: '',
                accessor: 'action' as any,
                fixed: 'right',
                width: 40,
                renderCell: ({ dataTestId }) => (
                  <AddCircle
                    color={disableInputs ? 'disabled' : 'primary'}
                    className={`z-10 ${disableInputs ? '' : 'cursor-pointer'}`}
                    onClick={() =>
                      disableInputs ? {} : handleAddingTime(time, isSentinel2 ? pressureType : pressure, timeType)
                    }
                    data-testid={`${dataTestId}-add-row-button`}
                  />
                ),
              },
            ]}
            data={[{ timeType, time, pressure }]}
            customRowHeight={PEGASUS_CUSTOM_ROW_HEIGHT + 8}
            freeColMinWidth={PEGASUS_FREE_COL_MIN_WIDTH}
            customHeaderHeight={PEGASUS_HEADER_HEIGHT}
            heightToContent
            dataTestId={`${dataTestId}-add-new-entry-table`}
          />
        </div>
      )}
    </>
  );
};

//

const handleSortTable = (a: PegasusTimeStatusesModel, b: PegasusTimeStatusesModel) => {
  const order = Object.values(TimeTypeType);
  const timeTypeOrderIndicator = order.indexOf(a?.timeType) - order.indexOf(b?.timeType);

  const timeOrderIndicator =
    new Date(0, 0, 0, getHours(a?.time), getMinutes(a?.time)).getTime() -
    new Date(0, 0, 0, getHours(b?.time), getMinutes(b?.time)).getTime();

  return timeTypeOrderIndicator === 0 ? timeOrderIndicator : timeTypeOrderIndicator;
};
